import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, KeyRound, XCircle, Crown } from "lucide-react";

/** Compact subscription status banner shown on Dashboard for all logged-in users. */
export default function SubscriptionBanner() {
  const [sub, setSub] = useState(null);
  useEffect(() => {
    api.get("/subscriptions/current").then(r => setSub(r.data)).catch(()=>{});
  }, []);

  if (!sub) return null;

  // Sold companies: perpetual ownership — no expiry, no renewal, no warnings
  if (sub.sold || sub.reason === "sold_perpetual") {
    return (
      <div className="border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-md p-3 flex items-center gap-3" data-testid="sub-banner-sold">
        <Crown className="w-6 h-6 text-amber-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-amber-900">ملكية دائمة — لا اشتراك ولا تجديد</div>
          <div className="text-xs text-amber-800 mt-0.5">هذه الشركة مملوكة ملكية كاملة ولا تتطلب أي اشتراك.</div>
        </div>
      </div>
    );
  }

  const exp = sub.effective_expires_at ? new Date(sub.effective_expires_at) : null;
  const now = new Date();
  const days = exp ? Math.max(0, Math.round((exp - now) / 86400000)) : 0;

  if (!sub.active) {
    return (
      <div className="border-2 border-red-400 bg-red-50 rounded-md p-3 flex items-start gap-3" data-testid="sub-banner-expired">
        <XCircle className="w-6 h-6 text-red-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-red-900">اشتراك الشركة منتهي ❌</div>
          <div className="text-xs text-red-700 mt-0.5">لا يمكنك إنشاء فواتير أو تسجيل عمليات جديدة. تواصل مع المدير العام لتجديد الاشتراك.</div>
        </div>
        <Link to="/payments"><button className="text-xs bg-red-600 text-white px-3 py-1.5 rounded font-bold" data-testid="sub-pay-btn">تسجيل دفعة</button></Link>
      </div>
    );
  }

  if (sub.reason === "override") {
    return (
      <div className="border border-amber-300 bg-amber-50 rounded-md p-3 flex items-start gap-3" data-testid="sub-banner-override">
        <KeyRound className="w-5 h-5 text-amber-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-amber-900">حسابك مفتوح يدوياً من المدير العام 🎁</div>
          <div className="text-xs text-amber-800">ينتهي الفتح المؤقت في {exp?.toLocaleDateString("ar-EG")} ({days} يوم متبقي).</div>
        </div>
      </div>
    );
  }

  if (days <= 7) {
    return (
      <div className="border border-amber-300 bg-amber-50 rounded-md p-3 flex items-start gap-3" data-testid="sub-banner-expiring">
        <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-amber-900">اشتراكك ينتهي قريباً ⏰</div>
          <div className="text-xs text-amber-800">
            الخطة: {sub.plan_name || "—"} · ينتهي بعد {days} يوم ({exp?.toLocaleDateString("ar-EG")})
          </div>
        </div>
        <Link to="/payments"><button className="text-xs bg-amber-600 text-white px-3 py-1.5 rounded font-bold" data-testid="sub-renew-btn">تجديد الآن</button></Link>
      </div>
    );
  }

  return (
    <div className="border border-emerald-200 bg-emerald-50 rounded-md p-2.5 flex items-center gap-2 text-xs" data-testid="sub-banner-active">
      <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0" />
      <span className="text-emerald-900 font-bold">اشتراك نشط{sub.plan_name ? ` — ${sub.plan_name}` : ""}</span>
      <span className="text-emerald-800">· ينتهي في {exp?.toLocaleDateString("ar-EG")} ({days} يوم)</span>
    </div>
  );
}
